import { supabase } from './supabase';

// Order Status Types
export const ORDER_STATUS = {
  PROCESSING: 'processing',
  SHIPPED: 'shipped',
  IN_TRANSIT: 'in_transit',
  OUT_FOR_DELIVERY: 'out_for_delivery',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled'
};

// Create order with tracking
export const createOrder = async (orderData) => {
  try {
    // Generate tracking number
    const trackingNumber = `BRL-${Date.now()}${Math.floor(Math.random() * 1000)}`;
    
    const { data, error } = await supabase
      .from('orders')
      .insert({
        ...orderData,
        tracking_number: trackingNumber,
        status: ORDER_STATUS.PROCESSING,
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    
    // Create initial tracking event
    await createTrackingEvent(data.id, {
      status: ORDER_STATUS.PROCESSING,
      description: 'Order has been received and is being processed',
      location: 'California, USA'
    });
    
    return data;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

// Track order by tracking number
export const trackOrder = async (trackingNumber) => {
  try {
    // Get order details
    const { data: order, error: orderError } = await supabase
      .from('orders')
      .select(`
        *,
        tracking_events (
          status,
          description,
          location,
          created_at
        )
      `)
      .eq('tracking_number', trackingNumber.toUpperCase())
      .single();

    if (orderError || !order) {
      return { error: 'Order not found. Please check your tracking number.' };
    }

    // Sort tracking events by date (newest first)
    const events = order.tracking_events?.sort((a, b) => 
      new Date(b.created_at) - new Date(a.created_at)
    ) || [];

    return {
      order: {
        tracking_number: order.tracking_number,
        status: order.status,
        created_at: order.created_at,
        estimated_delivery: order.estimated_delivery,
        shipping_address: order.shipping_address,
        items: order.items
      },
      events
    };
  } catch (error) {
    console.error('Error tracking order:', error);
    return { error: 'Unable to track order at this time. Please try again.' };
  }
};

// Create tracking event
export const createTrackingEvent = async (orderId, eventData) => {
  try {
    const { data, error } = await supabase
      .from('tracking_events')
      .insert({
        order_id: orderId,
        ...eventData,
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating tracking event:', error);
    throw error;
  }
};

// Update order status
export const updateOrderStatus = async (orderId, status, description, location = 'California, USA') => {
  try {
    // Update order status
    const { error: orderError } = await supabase
      .from('orders')
      .update({ status })
      .eq('id', orderId);

    if (orderError) throw orderError;

    // Add tracking event
    await createTrackingEvent(orderId, {
      status,
      description,
      location
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
};

// Get estimated delivery date (3-5 business days for USA)
export const getEstimatedDelivery = (orderDate) => {
  const date = new Date(orderDate);
  const businessDays = 5; // Maximum business days
  let count = 0;
  
  while (count < businessDays) {
    date.setDate(date.getDate() + 1);
    // Skip weekends
    if (date.getDay() !== 0 && date.getDay() !== 6) {
      count++;
    }
  }
  
  return date.toISOString();
};